import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { tr } from "@/lib/i18n";
import { music, sfx } from "@/lib/audio";
import { useTick } from "@/lib/hooks";
import { preloadSprites } from "@/lib/customers";
import { Stage } from "@/components/game/Stage";
import { LobbyScene } from "@/components/game/LobbyScene";
import { IntroScene } from "@/components/game/IntroScene";
import { DialogueScene } from "@/components/game/DialogueScene";
import { ScoreboardScene } from "@/components/game/ScoreboardScene";
import { PodiumScene } from "@/components/game/PodiumScene";
import { HostControls } from "@/components/game/HostControls";

function useHostGame(gameId) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const offset = useRef(0);
  const busy = useRef(false);
  const load = useCallback(async () => {
    const { data: d } = await api.get(`/games/${gameId}/host`);
    offset.current = d.server_now - Date.now();
    setData(d);
  }, [gameId]);
  useEffect(() => {
    load().catch((e) => setError(errMsg(e)));
    const id = setInterval(() => load().catch(() => {}), 900);
    return () => clearInterval(id);
  }, [load]);
  const g = data?.game;
  const next = useCallback(async () => {
    if (!g || busy.current) return;
    busy.current = true;
    try {
      await api.post(`/games/${gameId}/next`, { expected_phase: g.phase, expected_idx: g.idx });
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      busy.current = false;
    }
  }, [g, gameId, load]);
  return { data, error, offset, next, load };
}

function useHostSounds(g, now) {
  const phaseKey = g ? `${g.phase}:${g.idx}` : "";
  const musicOn = g?.quiz.settings.music;
  useEffect(() => {
    if (!g) return;
    if (["lobby", "intro", "scoreboard"].includes(g.phase)) (musicOn ? music.play("lobby") : music.stop());
    if (g.phase === "question") (musicOn ? music.play("lobby") : music.stop());
    if (g.phase === "reveal") { music.stop(); sfx.reveal(); }
    if (g.phase === "final") { music.stop(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseKey]);
  const shown = g?.phase === "question" && now >= g.options_at;
  useEffect(() => {
    if (shown) { sfx.whoosh(); if (musicOn) music.play("think"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);
  const secsLeft = shown ? Math.ceil((g.ends_at - now) / 1000) : null;
  useEffect(() => { if (secsLeft !== null && secsLeft <= 5 && secsLeft > 0) sfx.tick(); }, [secsLeft]);
  useEffect(() => () => music.stop(), []);
}

export default function Host() {
  const { gameId } = useParams();
  const nav = useNavigate();
  const { data, error, offset, next, load } = useHostGame(gameId);
  const now = useTick(200) + offset.current;
  const g = data?.game;
  const lang = g?.quiz.language || "en";
  const [endOpen, setEndOpen] = useState(false);
  useHostSounds(g, now);

  useEffect(() => { preloadSprites(); }, []);

  const prevJoinCount = useRef(null);
  useEffect(() => {
    if (!g || g.phase !== "lobby") { prevJoinCount.current = null; return; }
    const n = data.players.length;
    if (prevJoinCount.current !== null && n > prevJoinCount.current) sfx.join();
    prevJoinCount.current = n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.players.length, g?.phase]);

  useEffect(() => {
    if (!g) return;
    const elapsed = now - g.phase_started_at;
    if (g.phase === "question") {
      const all = data.players.length > 0 && data.answered >= data.players.length;
      if (now >= g.ends_at || (all && now >= g.options_at)) next();
    }
    if (g.phase === "intro" && elapsed > 12000) next();
    if (["reveal", "scoreboard"].includes(g.phase) && g.quiz.settings.auto_advance && elapsed > 8000) next();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now]);

  useEffect(() => {
    const onKey = (e) => {
      if (!g || !["Space", "Enter", "ArrowRight", "PageDown"].includes(e.code)) return;
      if (["reveal", "scoreboard"].includes(g.phase) || (g.phase === "lobby" && data.players.length > 0)) { e.preventDefault(); next(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [g, data, next]);

  const kick = async (p) => { await api.post(`/games/${gameId}/kick`, { player_id: p.id }); load(); };
  const skip = async () => {
    if (g.phase === "question" && now < g.options_at) { await api.post(`/games/${gameId}/skip-read`); load(); } else next();
  };
  const end = () => setEndOpen(true);
  const endWithScores = async () => { setEndOpen(false); await api.post(`/games/${gameId}/end`, { count_scores: true }); load(); };
  const endNoScores = async () => { setEndOpen(false); await api.post(`/games/${gameId}/end`, { count_scores: false }); nav("/admin"); };

  if (error) {
    return <Stage><div className="absolute inset-0 flex items-center justify-center font-press text-[1.6em] text-rose-300">{error}</div></Stage>;
  }
  if (!data) {
    return <Stage><div className="absolute inset-0 flex items-center justify-center font-press text-[2em] blink">{tr(lang, "loading")}</div></Stage>;
  }

  const f = g.idx >= 0 ? g.flat[Math.min(g.idx, g.flat.length - 1)] : null;
  const customer = f ? g.quiz.customers[f.c] : null;
  const question = f ? customer.questions[f.q] : null;
  const isDialogue = g.phase === "question" || g.phase === "reveal";
  const sceneKey = isDialogue ? `dlg-${g.idx}` : `${g.phase}-${g.idx}`;

  let scene = null;
  if (g.phase === "lobby") scene = <LobbyScene game={g} players={data.players} lang={lang} onStart={next} onKick={kick} />;
  if (g.phase === "intro") scene = <IntroScene customer={customer} number={f.c + 1} shirtColor={g.quiz.shirt_color} lang={lang} onDone={next} />;
  if (isDialogue) {
    scene = (
      <DialogueScene game={g} customer={customer} question={question} now={now} answered={data.answered}
        playerCount={data.players.length} counts={data.counts} reveal={g.phase === "reveal"} lang={lang} onNext={next} />
    );
  }
  if (g.phase === "scoreboard") {
    scene = <ScoreboardScene players={data.players} lang={lang} onNext={next} isLast={g.idx >= g.flat.length - 1} shirtColor={g.quiz.shirt_color} />;
  }
  if (g.phase === "final") scene = <PodiumScene players={data.players} lang={lang} shirtColor={g.quiz.shirt_color} onExit={() => nav("/admin")} />;

  return (
    <Stage testId="host-stage">
      <AnimatePresence mode="wait">
        <motion.div key={sceneKey} className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
          {scene}
        </motion.div>
      </AnimatePresence>
      <HostControls lang={lang} canSkip={g.phase === "intro" || g.phase === "question"} onSkip={skip} onEnd={end} showEnd={g.phase !== "final"} />
      {endOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" data-testid="host-end-dialog" onClick={() => setEndOpen(false)}>
          <div className="w-full max-w-md bg-[#12131f] border-4 border-[#FFD700] rounded-xl p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="font-press text-lg text-[#FFD700] mb-2">{tr(lang, "end_title")}</div>
            <div className="font-pixel text-xl text-slate-200 mb-6">{tr(lang, "end_scores_q")}</div>
            <div className="flex flex-col gap-3">
              <button data-testid="end-with-scores-button" className="px-btn text-sm !bg-[#26890C]" onClick={endWithScores}>{tr(lang, "end_count_yes")}</button>
              <button data-testid="end-no-scores-button" className="px-btn text-sm !bg-[#B0322B]" onClick={endNoScores}>{tr(lang, "end_count_no")}</button>
              <button data-testid="end-cancel-button" className="font-pixel text-lg text-slate-400 hover:text-slate-200 mt-1" onClick={() => setEndOpen(false)}>{tr(lang, "end_cancel")}</button>
            </div>
          </div>
        </div>
      )}
    </Stage>
  );
}
